import React from "react";
import { useTranslation } from "react-i18next";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import { trackSectionViewed, useSectionOnceObserver } from "../../lib/analytics";
import { WPL_SECTION } from "./sections.js";

/**
 * Questions about wedding planning with Agapaé.
 *
 * A plain definition list, always expanded — same reasoning as the two sibling
 * FAQs: every answer is short enough to read in place, and these are the page's
 * most self-contained factual passages, which is exactly what an assistant
 * quotes. Putting them behind an accordion would hide them from a reader and
 * gain nothing from a crawler.
 *
 * Every answer is checked against the implementation. The five that are easiest
 * to get wrong, and what the code actually says:
 *
 *  - `marketplace` — there is no vendor discovery anywhere in the product. The
 *    only way a vendor exists is `POST /organizer/vendors` with a name the
 *    couple typed. Nothing is contacted, booked or paid through Agapaé, so the
 *    answer says all three plainly.
 *  - `budget` — `OrganizerPayment` is a line the couple keeps, with a status of
 *    exactly `due` or `paid` (models.mjs). No document is issued to anybody and
 *    no money moves, which is why "not accounting software, and not invoicing"
 *    is stated rather than implied.
 *  - `documents` — the allowlist is PDF + images at 20 MB
 *    (`DOCUMENT_MIME_TYPES` / `DOCUMENT_MAX_BYTES`), magic-byte verified, and
 *    downloads are short-lived presigned ATTACHMENTS. "Up to 20 MB" is literal.
 *  - `timeline` — only three steps track themselves (`rsvp_completed`,
 *    `seating_completed`, `website_published` in utils/organizerSync.mjs). The
 *    answer names those three and claims nothing else updates itself.
 *  - `try` — the sandbox is a real seeded Heirloom event, but Éros is blocked
 *    inside it. The exclusion is stated in the same breath as the offer.
 *
 * `website` and `marketplace` are the two disambiguation answers: the only
 * places on this page where "wedding website builder" and "marketplace" are
 * allowed to appear at all. The forbidden-claims test pins those exceptions to
 * these keys.
 */
const FAQ_KEYS = [
  "what",
  "whatCanManage",
  "vendors",
  "marketplace",
  "tasks",
  "budget",
  "categories",
  "documents",
  "decisions",
  "timeline",
  "eros",
  "try",
  "plan",
  "website",
  "baptism",
];

const PlanningFaq = () => {
  const { t } = useTranslation();
  const analyticsContext = useAnalyticsContext();

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(WPL_SECTION.FAQ, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="wps-section-header">
          <h2 className="heading-50">{t("weddingPlanning.faq.heading")}</h2>
        </div>

        <dl className="wps-faq">
          {FAQ_KEYS.map((key) => (
            <div key={key} className="wps-faq-item">
              <dt className="made-22 wps-faq-q">
                {t(`weddingPlanning.faq.items.${key}.q`)}
              </dt>
              <dd className="wps-faq-a body-15">
                {t(`weddingPlanning.faq.items.${key}.a`)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

export default PlanningFaq;
